import { useEffect, useState } from "react";
import { sendOtpToEmailThunk } from "../../../store/auth/authThunk";
import { useDispatch, useSelector } from "react-redux";
import { clearAlerts } from "../../../store/alert/alertSlice";
import dizeeLogo from "../../../assets/images/dizee logo.svg";
import TypeWriter from "../../../components/OnBoard/Typewriter";
import { NextButton } from "../../../components/DizeeComponent/NextButton";
import styled from "styled-components";
import AccessibilityWidget from "../../../components/AccessibilityWidget";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 350px;
  margin: 0 auto;
  padding-left: 35px;
  padding-top: 0;
  overflow: hidden;
  height: 100vh;
  position: relative;
  touch-action: none;
`;

const FormSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-bottom: 80px;
  gap: 2px;
  margin-top: -40px;
  height: 100%;
  position: relative;
  padding-top: 40px;
`;

const ButtonContainer = styled.div`
  height: 40px;
  margin-top: 40px;
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  display: flex;
  align-items: center;
`;

const Footer = styled.div`
  position: fixed;
  bottom: 27px;
  width: 280px;
`;

const LogoImage = styled.img`
  width: 48px;
  height: auto;
  margin-bottom: 16px;
`;

const FooterText = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  max-width: 280px;
  line-height: 1.5;
`;

const EmailDisplay = styled.div`
  background: transparent;
  font-size: 12px;
  font-family: Helvetica;
  font-weight: 400;
  line-height: 14.32px;
  color: rgba(255, 255, 255, 0.6);
  margin: 20px 0;
  padding: 0;
  text-align: left;
`;

const Step0 = ({ setStep, setUser, user }) => {
  const dispatch = useDispatch();
  const { loader } = useSelector((state) => state.alert);
  const { email } = useSelector((state) => state.user);
  const [isDisabled, setIsDisabled] = useState(false);

  // Use email from Redux state or user prop
  const userEmail = user?.email || email;

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    document.body.style.touchAction = 'none';
    
    return () => {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
      document.body.style.position = 'static';
      document.body.style.width = 'auto';
      document.body.style.height = 'auto';
      document.body.style.touchAction = 'auto';
    };
  }, []);

  useEffect(() => {
    // Ensure we have the email in the user object
    if (userEmail && !user?.email) {
      setUser({ ...user, email: userEmail });
    }
  }, [userEmail, user, setUser]);

  const sendVerifyCode = async (e) => {
    e.preventDefault();
    if (!userEmail) {
      console.error('No email address available');
      return;
    }

    setIsDisabled(true);
    dispatch(clearAlerts());
    
    try {
      const result = await dispatch(sendOtpToEmailThunk(userEmail)).unwrap();
      if (result.status === 200) {
        setStep(1);
      }
    } catch (error) {
      console.error('Email verification failed:', error);
    } finally {
      setIsDisabled(false);
    }
  };

  return (
    <Container>
      <FormSection>
        <TypeWriter text={"Send verification code to your email"} speed={45} />
        
        <EmailDisplay>
          {userEmail || 'No email address found'}
        </EmailDisplay>

        <form onSubmit={sendVerifyCode}>
          <ButtonContainer show={!!userEmail}>
            <NextButton
              disabled={isDisabled || loader || !userEmail}
              type="submit"
            >
              {loader ? 'Sending...' : 'Send verification code'}
            </NextButton>
          </ButtonContainer>
        </form>
      </FormSection>
      <Footer>
        <LogoImage src={dizeeLogo} alt="DIZEE" />
        <FooterText>
          We'll send a 6-digit verification code to your email address.
        </FooterText>
      </Footer>
      <AccessibilityWidget />
    </Container>
  );
};

export default Step0;
